import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Lock, CreditCard, AlertCircle } from "lucide-react";

function EditInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmStep, setConfirmStep] = useState(1); // 1: Escribir número, 2: Confirmación final
  const [deleteInput, setDeleteInput] = useState("");
  const [form, setForm] = useState({

    invoice_number: "",
    date: "",
    due_date: "",
    subtotal: "",
    iva: "",
    freight: "",
    retention: "",
    rete_ica: "",
    discount: "",
    glosa: "",
    glosa_retencion: "",
    glosa_reteica: "",
    glosa_total: 0,
    extra_concept: "",
    extra_value: "",
    total: "",
    balance: "",
    supplier_id: "",
    observacion: "",
    paid: 0,
    status: "pending",
  });

  const showGlosaExtras = Number(form.glosa || 0) > 0;

  // 🔹 Formatear números para la vista
  const formatNumberWithDots = (value) => {
    if (value === undefined || value === null || value === "") return "";

    // Convertimos a número flotante y quitamos decimales (asumiendo que manejas pesos enteros)
    const numberValue = Math.floor(parseFloat(value));

    if (isNaN(numberValue)) return "";

    // Formato con puntos para miles: 5316000 -> 5.316.000
    return new Intl.NumberFormat("es-CO", {
      maximumFractionDigits: 0,
    }).format(numberValue);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchInvoice(), fetchSuppliers()]);
      setLoading(false);
    };
    init();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const res = await api.get(`/invoices/${id}`);
      setForm({
        ...res.data,
        glosa_retencion: res.data.glosa_retencion || "",
        glosa_reteica: res.data.glosa_reteica || "",
        glosa_total: res.data.glosa_total ?? 0,
      });
      // Bloquear si tiene abonos (partial) o está pagada (paid)
      setIsLocked(res.data.status === "paid" || res.data.status === "partial");
    } catch {
      toast.error("Error al cargar la factura");
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data);
    } catch {
      toast.error("Error al cargar proveedores");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    // 🔴 CAMBIO AQUÍ: Permitir invoice_number, due_date y observacion si está bloqueado
    const allowedInLocked = ["invoice_number", "date", "due_date", "observacion"];
    if (isLocked && !allowedInLocked.includes(name)) return;
    let newValue = value;

    if (["subtotal", "iva", "freight", "retention", "rete_ica", "discount", "glosa", "glosa_retencion", "glosa_reteica", "extra_value"].includes(name)) {
      // 1. Quitamos TODO lo que no sea número (puntos de miles, comas, etc.)
      newValue = value.replace(/\D/g, "");

      // 2. Si el usuario borra todo, dejamos 0 para evitar NaN en cálculos
      if (newValue === "") newValue = "0";
    }

    const updatedForm = { ...form, [name]: newValue };

    if (!isLocked) {
      // Usamos Number() sobre el string limpio de solo dígitos
      const subtotal = Number(updatedForm.subtotal || 0);
      const iva = Number(updatedForm.iva || 0);
      const freight = Number(updatedForm.freight || 0);
      const extraValue = Number(updatedForm.extra_value || 0);
      const retention = Number(updatedForm.retention || 0);
      const reteIca = Number(updatedForm.rete_ica || 0);
      const discount = Number(updatedForm.discount || 0);
      const glosa = Number(updatedForm.glosa || 0);
      const glosaRetencion = Number(updatedForm.glosa_retencion || 0);
      const glosaReteica = Number(updatedForm.glosa_reteica || 0);

      const totalFactura = subtotal + iva + freight + extraValue;
      let glosaTotal = Math.max(0, glosa - glosaRetencion - glosaReteica);

      if (glosa <= 0) {
        updatedForm.glosa_retencion = "";
        updatedForm.glosa_reteica = "";
        glosaTotal = 0;
      }

      const valorAPagar = totalFactura - retention - reteIca - discount - glosaTotal;

      updatedForm.total = totalFactura;
      updatedForm.balance = valorAPagar;
      updatedForm.glosa_total = glosaTotal;
    }

    setForm(updatedForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    try {
      let dataToSave;

      if (isLocked) {
        // 🛡️ MODO PROTEGIDO: Solo enviamos el número para cumplir con la lógica del controlador
        dataToSave = {
          invoice_number: form.invoice_number.toString(),
          date: form.date, // 👈 Añadido
          due_date: form.due_date, // 👈 Añadido
          observacion: form.observacion // 👈 Añadido
        };
      } else {
        // 🛡️ MODO NORMAL: Enviamos todo el objeto limpio
        dataToSave = {
          invoice_number: form.invoice_number,
          date: form.date,
          due_date: form.due_date,
          supplier_id: Number(form.supplier_id),
          observacion: form.observacion,
          subtotal: Number(form.subtotal || 0),
          iva: Number(form.iva || 0),
          freight: Number(form.freight || 0),
          extra_concept: form.extra_concept,
          extra_value: Number(form.extra_value || 0),
          retention: Number(form.retention || 0),
          rete_ica: Number(form.rete_ica || 0),
          discount: Number(form.discount || 0),
          glosa: Number(form.glosa || 0),
          glosa_retencion: Number(form.glosa_retencion || 0),
          glosa_reteica: Number(form.glosa_reteica || 0),
          glosa_total: Number(form.glosa_total || 0),
          total: Number(form.total || 0),
          balance: Number(form.balance || 0),
        };
      }

      console.log("📤 Enviando datos:", dataToSave);

      await api.put(`/invoices/${id}`, dataToSave);
      toast.success("Factura actualizada correctamente");
      navigate("/invoices");

    } catch (error) {
      console.error("❌ Error del servidor:", error.response?.data);

      if (error.response?.data?.errors) {
        // Convertimos el array de errores en un objeto { campo: mensaje }
        const newErrors = {};
        error.response.data.errors.forEach((err) => {
          newErrors[err.path] = err.msg;
        });
        setErrors(newErrors);

        // Opcional: mostrar un toast general
        toast.error("Revisa los campos obligatorios");
      } else {
        const serverError = error.response?.data?.error || "Error al actualizar";
        toast.error(serverError);
      }
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Factura eliminada definitivamente");
      navigate("/invoices");
    } catch (error) {
      const msg = error.response?.data?.error || "Error al eliminar";
      toast.error(msg);
      setShowDeleteModal(false);
      setConfirmStep(1);
    }
  };

  const openDeleteModal = () => {
    setDeleteInput("");
    setConfirmStep(1);
    setShowDeleteModal(true);
  };

  if (loading) return <div className="p-10 text-center">Cargando...</div>;

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-4xl mx-auto">
      {/* Cabecera */}
      <div className="flex items-center justify-between mb-6">
        {/* LADO IZQUIERDO: Botón volver solo */}
        <button
          onClick={() => navigate("/invoices")}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition"
        >
          <ArrowLeft size={20} /> Volver
        </button>

        {/* LADO DERECHO: Grupo de botones de acción */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openDeleteModal}
            className="flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-2 rounded-lg text-red-600 hover:bg-red-100 transition"
          >
            <AlertCircle size={18} /> Eliminar Factura
          </button>

          <button
            onClick={() => navigate(`/invoices/${id}/payments`)}
            className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg text-blue-700 hover:bg-blue-100 transition"
          >
            <CreditCard size={18} /> Ver Pagos Realizados
          </button>
        </div>
      </div>

      <h2 className="text-3xl font-bold text-gray-800 mb-6">Editar Factura</h2>

      {/* Alerta de Bloqueo Parcial */}
      {isLocked && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg">
          <div className="flex items-center gap-3">
            <Lock className="text-amber-600" size={24} />
            <div>
              <p className="text-amber-800 font-semibold">Factura Protegida</p>
              <p className="text-amber-700 text-sm">
                Debido a que ya existen abonos o pagos, solo puedes modificar el <b>Número de Factura</b>. Los valores financieros están bloqueados.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Campo Siempre Editable */}
        <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <label className="block text-sm font-bold text-blue-900 mb-1 uppercase tracking-wider">
            Número de Factura (Referencia)
          </label>
          <input
            type="text"
            name="invoice_number"
            value={form.invoice_number}
            onChange={handleChange}
            className={`w-full border-2 px-4 py-3 rounded-lg text-lg font-mono outline-none transition-all shadow-sm ${errors.invoice_number
              ? "border-red-500 focus:border-red-600"
              : "border-blue-200 focus:border-blue-500"
              }`}
            placeholder="Ej: FAC-001"
          />
          {/* Mensaje de error debajo del input */}
          {errors.invoice_number && (
            <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
              <AlertCircle size={16} /> {errors.invoice_number}
            </p>
          )}
        </div>

        {/* Fechas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Emisión</label>
          <input
            type="date"
            name="date"
            value={form.date?.substring(0, 10) || ""}
            onChange={handleChange}
            disabled={false}
            max={new Date().toISOString().split("T")}
            className="w-full border px-4 py-2 rounded-lg disabled:bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Vencimiento</label>
          <input
            type="date"
            name="due_date"
            value={form.due_date?.substring(0, 10) || ""}
            onChange={handleChange}
            className="w-full border px-4 py-2 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
          />
        </div>

        {/* Proveedor */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
          <select
            name="supplier_id"
            value={form.supplier_id}
            onChange={handleChange}
            disabled={isLocked}
            className="w-full border px-4 py-2 rounded-lg disabled:bg-gray-100"
          >
            <option value="">Seleccione un proveedor</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Bloque de Valores Dinámicos (Campos Estándar) */}
        {[
          ["subtotal", "Subtotal"],
          ["iva", "IVA"],
          ["freight", "Flete"],
          ["retention", "Retención"],
          ["rete_ica", "Rete ICA"],
          ["discount", "Descuento"],
        ].map(([field, label]) => (
          <div key={field}>
            <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-400">$</span>
              <input
                type="text"
                name={field}
                value={formatNumberWithDots(form[field])}
                onChange={handleChange}
                disabled={isLocked}
                className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg disabled:bg-gray-50 disabled:text-gray-400 font-medium bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>
        ))}

        <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Concepto
              </label>

              <input
                type="text"
                name="extra_concept"
                value={form.extra_concept || ""}
                onChange={handleChange}
                disabled={isLocked}
                placeholder="Ej: Estampilla, AIU, Recargo..."
                className="w-full border border-gray-300 px-4 py-2 rounded-lg disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Valor
              </label>

              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400">$</span>

                <input
                  type="text"
                  name="extra_value"
                  value={formatNumberWithDots(form.extra_value)}
                  onChange={handleChange}
                  disabled={isLocked}
                  className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* RENDERIZADO CONDICIONAL DE GLOSA (MODO EDICIÓN) */}
        {!showGlosaExtras ? (
          /* MODO NORMAL: Se integra con los demás campos */
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Glosa</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-400">$</span>
              <input
                type="text"
                name="glosa"
                value={formatNumberWithDots(form.glosa)}
                onChange={handleChange}
                disabled={isLocked}
                className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg disabled:bg-gray-50 disabled:text-gray-400 font-medium bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>
        ) : (
          /* MODO DETALLE: Contenedor gris destacado que ocupa las 2 columnas */
          <div className="md:col-span-2 bg-gray-50 p-5 rounded-xl border border-gray-200 shadow-sm transition-all">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-4 bg-blue-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Detalle de Glosas</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {/* 1. Glosa Base */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Glosa Base</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="text"
                    name="glosa"
                    value={formatNumberWithDots(form.glosa)}
                    onChange={handleChange}
                    disabled={isLocked}
                    className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-400 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 2. Glosa Retención */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">G. Retención</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="text"
                    name="glosa_retencion"
                    value={formatNumberWithDots(form.glosa_retencion)}
                    onChange={handleChange}
                    disabled={isLocked}
                    className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-400 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 3. Glosa Rete ICA */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">G. Rete ICA</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="text"
                    name="glosa_reteica"
                    value={formatNumberWithDots(form.glosa_reteica)}
                    onChange={handleChange}
                    disabled={isLocked}
                    className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-400 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 4. Glosa Total */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Glosa Total</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="text"
                    name="glosa_total"
                    value={formatNumberWithDots(form.glosa_total)}
                    readOnly
                    className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-gray-200 text-gray-800 font-bold outline-none cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resultado Final */}
        <div className="bg-gray-800 text-white p-4 rounded-lg shadow-inner">
          <label className="block text-xs font-bold uppercase opacity-70 mb-1">Total a Pagar (Saldo)</label>
          <div className="text-2xl font-bold">
            $ {formatNumberWithDots(form.balance)}
          </div>
        </div>

        {/* Observaciones */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones Internas</label>
          <textarea
            name="observacion"
            value={form.observacion || ""}
            onChange={handleChange}
            // Quitamos el disabled={isLocked}
            rows={2}
            className="w-full border px-4 py-2 rounded-lg resize-none"
          />
        </div>

        {/* Botón de Acción */}
        <div className="md:col-span-2 pt-4 border-t">
          <button
            type="submit"
            className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-lg shadow-lg text-white bg-blue-600 hover:bg-blue-700 transition-all font-bold"
          >
            <Save size={20} />
            Guardar Cambios
          </button>
        </div>
      </form>
      {/* MODAL DE ELIMINACIÓN DE SEGURIDAD */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>

              {confirmStep === 1 ? (
                <>
                  <h3 className="text-xl font-bold text-gray-900">¿Eliminar esta factura?</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Esta acción es irreversible. Para continuar, por favor escribe el número de la factura: <br />
                    <span className="font-mono font-bold text-gray-800 bg-gray-100 px-2 rounded">{form.invoice_number}</span>
                  </p>
                  <input
                    type="text"
                    className="mt-4 w-full p-3 border-2 border-gray-200 rounded-xl focus:border-red-500 outline-none text-center font-bold"
                    placeholder="Escribe el número aquí"
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                  />
                  <div className="mt-6 flex gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">Cancelar</button>
                    <button
                      disabled={deleteInput !== form.invoice_number}
                      onClick={() => setConfirmStep(2)}
                      className={`flex-1 px-4 py-2 rounded-lg font-semibold transition ${deleteInput === form.invoice_number ? 'bg-red-600 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      Continuar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-gray-900 text-red">¿ESTÁS TOTALMENTE SEGURO?</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Se borrarán los registros de la factura <b>{form.invoice_number}.</b>
                    <br />
                    No podrás recuperar esta información.
                  </p>
                  <div className="mt-6 flex flex-col gap-3">
                    <button
                      onClick={handleDelete}
                      className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold shadow-lg"
                    >
                      SÍ, ELIMINAR DEFINITIVAMENTE
                    </button>
                    <button
                      onClick={() => setConfirmStep(1)}
                      className="w-full px-4 py-2 text-gray-500 hover:underline"
                    >
                      Regresar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditInvoice;